// legacy auth APIs
import { generateOTP, resendOTP, login } from 'shared/api/otp';
import { getLoggedInUser } from 'shared/api/auth';
import { getAddresses } from 'shared/api/address';

// Grahak (JWT) auth APIs
import {
  sendOtp,
  resendOtpJwt,
  verifyLoginJwt,
  getGuestSession,
  refreshToken,
  getLoggedInUserJwt,
  getAddressesJwt,
  checkAccountDeleteEligibilityJwt,
} from 'shared/api/authJwt';
import { isGuestSessionMarked, markGuestInitialized } from 'shared/api/grahakSession';

// account-delete APIs (legacy + Grahak variants picked at runtime)
import {
  checkUserAccountDeleteEligibility,
  sendAccountDeleteOtp,
  sendAccountDeleteOtpGrahak,
  resendAccountDeleteOtp,
  resendAccountDeleteOtpGrahak,
  deleteUserAccount,
  deleteUserAccountGrahak,
} from 'shared/api/accountDelete';

// profile APIs (legacy + Grahak variants picked at runtime)
import {
  saveProfileDataLegacy,
  saveProfileDataGrahak,
  uploadProfilePicLegacy,
  uploadProfilePicGrahak,
  getProfileDataLegacy,
  getProfileDataGrahak,
  getProfileImageLegacy,
  getProfileImageGrahak,
} from 'shared/api/profile';

// helpers
import { setGuestSessionInitializing } from 'shared/helpers/api';
import { trackGrahakFunctionality } from 'shared/helpers/grahakMonitoring';
import {
  GRAHAK_AUTH_SURFACE,
  GrahakAuthSurface,
  getGrahakAuthSurfaceFromState,
  isEligibleforGrahakRuleFromState,
} from 'shared/api/grahakAuthSurface';

// store
import { getStore } from 'shared/store';
import { setGuestSession } from 'shared/store/app/actions';
import { getRemoteConfig } from 'shared/store/remoteConfigs/selectors';

export { GRAHAK_AUTH_SURFACE };
export type { GrahakAuthSurface };

export const getGrahakAuthSurface = (): GrahakAuthSurface | null => {
  return getGrahakAuthSurfaceFromState(getStore()?.getState());
};

export const isEligibleforGrahakRule = () =>
  isEligibleforGrahakRuleFromState(getStore()?.getState());

const DEFAULT_GUEST_SESSION_MAX_RETRIES = 2;
const MAX_GUEST_SESSION_RETRIES = 5;

const getGuestSessionMaxRetries = (): number => {
  const state = getStore()?.getState();
  const { guestSessionMaxRetries } = (state && getRemoteConfig(state, 'grahakAuth')) || {};
  const retries = Number(guestSessionMaxRetries);
  if (!Number.isInteger(retries) || retries < 0) {
    return DEFAULT_GUEST_SESSION_MAX_RETRIES;
  }

  return Math.min(retries, MAX_GUEST_SESSION_RETRIES);
};

type AnyFn = (...args: any[]) => any;
function pick<T extends AnyFn, U extends AnyFn>(legacyFn: T, jwtFn: U, functionality: string) {
  return (...args: Parameters<T | U>): ReturnType<T | U> => {
    const isGrahakEnabled = isEligibleforGrahakRule();
    const result = (isGrahakEnabled ? (jwtFn as AnyFn) : (legacyFn as AnyFn))(...args);

    if (!isGrahakEnabled || !result || typeof result.catch !== 'function') {
      return result;
    }

    return result.catch((error: unknown) => {
      trackGrahakFunctionality({
        functionality,
        outcome: 'failure',
        reason: 'operation_rejected',
        error,
      });
      throw error;
    });
  };
}

const getLoggedInUserCompat = async (...args: any[]) => {
  const data = await (getLoggedInUserJwt as AnyFn)(...args);

  if (data?.response?.customerData) {
    return data;
  }

  const customerData = data?.customerData ?? data?.response ?? data;

  return {
    ...data,
    response: {
      ...(data?.response && typeof data.response === 'object' ? data.response : {}),
      customerData,
    },
  };
};

const getAddressesCompat = async (...args: any[]) => {
  const response = await (getAddressesJwt as AnyFn)(...args);

  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.response)) {
    return response.response;
  }

  if (Array.isArray(response?.addresses)) {
    return response.addresses;
  }

  if (Array.isArray(response?.response?.addresses)) {
    return response.response.addresses;
  }

  return response;
};

type GuestSessionInitResult = {
  status?: string;
  [key: string]: unknown;
};

type GuestSessionRuntimeState = {
  forceGuest: boolean;
  promise: Promise<GuestSessionInitResult>;
};

let activeGuestSessionInit: GuestSessionRuntimeState | null = null;
let queuedForcedGuestInit: Promise<GuestSessionInitResult> | null = null;

const executeGuestSessionInit = async (forceGuest: boolean): Promise<GuestSessionInitResult> => {
  setGuestSessionInitializing(true);

  try {
    if (!forceGuest && isGuestSessionMarked()) {
      getStore()?.dispatch(setGuestSession());
      return { status: 'active' };
    }

    const response = await getGuestSession(0, getGuestSessionMaxRetries());

    getStore()?.dispatch(setGuestSession());
    markGuestInitialized();

    return response;
  } catch (error) {
    trackGrahakFunctionality({
      functionality: 'guest_session_initialization',
      outcome: 'failure',
      reason: 'retry_budget_exhausted',
      error,
    });
    getStore()?.dispatch(setGuestSession());
    return Promise.reject({ status: 'failed', error });
  } finally {
    setGuestSessionInitializing(false);
  }
};

const startGuestSessionInit = (forceGuest: boolean): Promise<GuestSessionInitResult> => {
  const promise = executeGuestSessionInit(forceGuest);
  activeGuestSessionInit = { forceGuest, promise };

  const clearActiveInit = () => {
    if (activeGuestSessionInit?.promise === promise) {
      activeGuestSessionInit = null;
    }
  };
  void promise.then(clearActiveInit, clearActiveInit);

  return promise;
};

const initGuestSession = (options?: { forceGuest?: boolean }): Promise<GuestSessionInitResult> => {
  if (!isEligibleforGrahakRule()) {
    return Promise.resolve({ status: 'skipped' });
  }

  const forceGuest = Boolean(options?.forceGuest);

  if (!activeGuestSessionInit && queuedForcedGuestInit) {
    return queuedForcedGuestInit;
  }

  if (!activeGuestSessionInit) {
    return startGuestSessionInit(forceGuest);
  }

  if (!forceGuest || activeGuestSessionInit.forceGuest) {
    return activeGuestSessionInit.promise;
  }

  if (!queuedForcedGuestInit) {
    queuedForcedGuestInit = activeGuestSessionInit.promise
      .catch(() => undefined)
      .then(() => startGuestSessionInit(true))
      .finally(() => {
        queuedForcedGuestInit = null;
      });
  }

  return queuedForcedGuestInit;
};

export const AuthAPI = {
  sendOtp: pick(generateOTP, sendOtp, 'send_otp'),
  resendOtp: pick(resendOTP, resendOtpJwt, 'resend_otp'),
  verifyOtp: pick(login, verifyLoginJwt, 'verify_otp'),
  getLoggedInUser: pick(getLoggedInUser, getLoggedInUserCompat, 'get_user'),
  getAddresses: pick(getAddresses, getAddressesCompat, 'get_addresses'),
  checkAccountDeleteEligibility: pick(
    checkUserAccountDeleteEligibility,
    checkAccountDeleteEligibilityJwt,
    'account_delete_eligibility'
  ),
  sendAccountDeleteOtp: pick(
    sendAccountDeleteOtp,
    sendAccountDeleteOtpGrahak,
    'send_account_delete_otp'
  ),
  resendAccountDeleteOtp: pick(
    resendAccountDeleteOtp,
    resendAccountDeleteOtpGrahak,
    'resend_account_delete_otp'
  ),
  deleteUserAccount: pick(deleteUserAccount, deleteUserAccountGrahak, 'delete_account'),
  saveProfileData: pick(saveProfileDataLegacy, saveProfileDataGrahak, 'save_profile'),
  uploadProfilePic: pick(uploadProfilePicLegacy, uploadProfilePicGrahak, 'upload_profile_picture'),
  getProfileData: pick(getProfileDataLegacy, getProfileDataGrahak, 'get_profile_data'),
  getProfileImage: pick(getProfileImageLegacy, getProfileImageGrahak, 'get_profile_image'),
  initGuestSession,
  refreshJwtToken: () => (isEligibleforGrahakRule() ? refreshToken() : Promise.resolve()),
};
